import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type { PhysicalStrandPayload } from '@/data/sampleDemoResult';

// Types
export type AssessmentMode = 'presenting' | 'explaining' | 'persuading';
export type AssessmentStatus = 'pending' | 'uploading' | 'processing' | 'draft_ready' | 'under_review' | 'signed_off' | 'error';

export interface PromptTemplate {
  id: string;
  name: string;
  mode: AssessmentMode;
  age_band: string;
  instructions: string;
  time_limit_seconds: number;
  requires_topic: boolean;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
}

export interface Assessment {
  id: string;
  student: string;
  student_name: string;
  cohort: string;
  cohort_name: string;
  mode: AssessmentMode;
  prompt: PromptTemplate | null;
  prompt_name: string | null;
  status: AssessmentStatus;
  time_limit_seconds: number;
  consent_obtained: boolean;
  has_recording: boolean;
  physical_score_visible_to_student?: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
  uploaded_at: string | null;
  completed_at: string | null;
}

export interface AssessmentListItem {
  id: string;
  student_name: string;
  cohort: string;
  cohort_name: string;
  mode: AssessmentMode;
  status: AssessmentStatus;
  prompt: string | PromptTemplate | null;
  prompt_name: string | null;
  time_limit_seconds: number;
  consent_obtained: boolean;
  has_recording: boolean;
  status_message: string;
  created_at: string;
  updated_at: string;
  uploaded_at: string | null;
  completed_at: string | null;
}

export interface TranscriptWord {
  word: string;
  start: number;
  end: number;
  confidence: number | null;
}

export interface TranscriptSegment {
  start: number;
  end: number;
  text: string;
  words: TranscriptWord[];
}

export interface Transcript {
  segments: TranscriptSegment[];
  full_text: string;
  language: string;
  confidence: number;
  provider?: string;
  model_version?: string;
}

export interface FeatureSignals {
  // Fill in based on actual server response
  [key: string]: unknown;
}

export interface EvidenceCandidate {
  candidate_id: string;
  start_time: number;
  end_time: number;
  type: string;
  summary: string;
  transcript_text: string;
  features: Record<string, unknown>;
  relevant_strands: string[];
}

export interface DraftReport {
  id: string;
  physical_score: {
    band: string;  // 5-band scale
    score: number;  // 1-5 scale (from deterministic physical scoring)
    confidence: number;
    justification: string;
    subskills: Record<string, {
      score: number;  // 1-5 scale
      band: string;   // 5-band scale
    }>;
    evidence_clips?: string[];
  };
  linguistic_score: {
    band: string;
    score: number;
    confidence: number;
    justification: string;
    subskills: Record<string, unknown>;
    evidence_clips?: string[];
  };
  cognitive_score: {
    band: string;
    score: number;
    confidence: number;
    justification: string;
    subskills: Record<string, unknown>;
    evidence_clips?: string[];
  };
  social_emotional_score: {
    band: string;
    score: number;
    confidence: number;
    justification: string;
    subskills: Record<string, unknown>;
    evidence_clips?: string[];
  };
  feedback: {
    strengths: string[];
    next_steps: string[];
    goals: string[];
  };
  overall_confidence: number;
  warnings: string[];
  eal_scaffolds: string[];
  is_reviewed: boolean;
  ai_model: string;
  generated_at: string;
}

export interface SignedReport extends Omit<DraftReport, 'is_reviewed'> {
  teacher_notes: string;
  signed_by: string;
  signed_at: string;
  changes_summary: string | null;
}

export interface CreateAssessmentRequest {
  student_id: string;
  cohort_id: string;
  mode: AssessmentMode;
  prompt_id?: string;
  time_limit_seconds?: number;
}

export interface SignOffRequest {
  physical_band?: number;
  physical_clips?: string[];
  linguistic_band?: number;
  linguistic_clips?: string[];
  cognitive_band?: number;
  cognitive_clips?: string[];
  social_emotional_band?: number;
  social_emotional_clips?: string[];
  feedback_strengths?: string[];
  feedback_next_steps?: string[];
  feedback_goals?: string[];
  teacher_notes?: string;
}

const TRANSIENT_STATUSES: AssessmentStatus[] = ['pending', 'uploading', 'processing'];

// Assessment hooks
export function useAssessments() {
  return useQuery<AssessmentListItem[]>({
    queryKey: ['assessments'],
    queryFn: async () => {
      const response = await apiClient.get<AssessmentListItem[] | { results: AssessmentListItem[] }>('/assessments/');
      // Handle paginated response
      if (response.data && typeof response.data === 'object' && 'results' in response.data) {
        return response.data.results;
      }
      return response.data;
    },
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      const hasTransient = data.some((a) => TRANSIENT_STATUSES.includes(a.status));
      return hasTransient ? 5000 : false;
    },
  });
}

export function useAssessment(id: string) {
  return useQuery<Assessment>({
    queryKey: ['assessment', id],
    queryFn: async () => {
      const response = await apiClient.get<Assessment>(`/assessments/${id}/`);
      return response.data;
    },
    enabled: !!id,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status !== undefined && TRANSIENT_STATUSES.includes(status) ? 3000 : false;
    },
  });
}

export function useCreateAssessment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateAssessmentRequest): Promise<Assessment> => {
      const response = await apiClient.post<Assessment>('/assessments/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessments'] });
    },
  });
}

// Self-registered learners (no teacher) start their own Physical Strand task.
// Returns a fresh pending assessment ready to record (or an existing untouched one).
export function useStartSelfServeAssessment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<Assessment> => {
      const response = await apiClient.post<Assessment>('/assessments/start_self_serve/', {});
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessments'] });
    },
  });
}

export interface BulkCreateAssessmentRequest {
  cohort_id: string;
  mode: AssessmentMode;
  prompt_id?: string;
  time_limit_seconds?: number;
}

export function useBulkCreateAssessment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BulkCreateAssessmentRequest): Promise<{ count: number }> => {
      const response = await apiClient.post<{ count: number }>('/assessments/bulk_create/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessments'] });
    },
  });
}

export interface GetUploadUrlRequest {
  filename: string;
  content_type: string;
  file_size: number;
}

export interface GetUploadUrlResponse {
  upload_url: string;
  file_key: string;
  assessment_id: string;
  expires_in: number;
}

export function useGetUploadUrl() {
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: GetUploadUrlRequest }): Promise<GetUploadUrlResponse> => {
      const response = await apiClient.post<GetUploadUrlResponse>(`/assessments/${id}/get_upload_url/`, data);
      return response.data;
    },
  });
}

export function useUpdateAssessment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Record<string, unknown> }): Promise<Assessment> => {
      const response = await apiClient.patch<Assessment>(`/assessments/${id}/`, data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['assessments'] });
      queryClient.setQueryData(['assessment', data.id], data);
    },
  });
}

export function useDeleteAssessment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/assessments/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessments'] });
    },
  });
}

export function useUploadRecording() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ assessmentId, file }: { assessmentId: string; file: File }) => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post(`/assessments/${assessmentId}/upload_recording/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['assessment', variables.assessmentId] });
      queryClient.invalidateQueries({ queryKey: ['assessments'] });
    },
  });
}

export function useProcessAssessment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (assessmentId: string) => {
      const response = await apiClient.post(`/assessments/${assessmentId}/process/`);
      return response.data;
    },
    onSuccess: (_, assessmentId) => {
      queryClient.invalidateQueries({ queryKey: ['assessment', assessmentId] });
      queryClient.invalidateQueries({ queryKey: ['assessments'] });
    },
  });
}


export function useRetryAssessment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (assessmentId: string) => {
      const response = await apiClient.post(`/assessments/${assessmentId}/process/`);
      return response.data;
    },
    onSuccess: (_, assessmentId) => {
      queryClient.invalidateQueries({ queryKey: ['assessments'] });
      queryClient.invalidateQueries({ queryKey: ['assessment', assessmentId] });
    },
  });
}

export function useAssessmentTranscript(assessmentId: string) {
  return useQuery<Transcript>({
    queryKey: ['assessmentTranscript', assessmentId],
    queryFn: async () => {
      const response = await apiClient.get<Transcript>(`/assessments/${assessmentId}/transcript/`);
      return response.data;
    },
    enabled: !!assessmentId,
  });
}

export function useAssessmentFeatures(assessmentId: string) {
  return useQuery<FeatureSignals>({
    queryKey: ['assessmentFeatures', assessmentId],
    queryFn: async () => {
      const response = await apiClient.get<FeatureSignals>(`/assessments/${assessmentId}/features/`);
      return response.data;
    },
    enabled: !!assessmentId,
  });
}

export function useAssessmentPhysicalResult(assessmentId: string, enabled = true) {
  return useQuery<PhysicalStrandPayload>({
    queryKey: ['assessmentPhysicalResult', assessmentId],
    queryFn: async () => {
      const response = await apiClient.get<PhysicalStrandPayload>(
        `/assessments/${assessmentId}/physical_result/`,
      );
      return response.data;
    },
    enabled: !!assessmentId && enabled,
    retry: false,
  });
}

export function useAssessmentEvidence(assessmentId: string) {
  return useQuery<EvidenceCandidate[]>({
    queryKey: ['assessmentEvidence', assessmentId],
    queryFn: async () => {
      const response = await apiClient.get<EvidenceCandidate[]>(`/assessments/${assessmentId}/evidence/`);
      return response.data;
    },
    enabled: !!assessmentId,
  });
}

export function useRecordingUrl(assessmentId: string) {
  return useQuery<string>({
    queryKey: ['recordingUrl', assessmentId],
    queryFn: async () => {
      const response = await apiClient.get<{ url: string }>(`/assessments/${assessmentId}/recording_url/`);
      return response.data.url;
    },
    enabled: !!assessmentId,
  });
}

export function useAssessmentDraftReport(assessmentId: string) {
  return useQuery<DraftReport>({
    queryKey: ['assessmentDraftReport', assessmentId],
    queryFn: async () => {
      const response = await apiClient.get<DraftReport>(`/assessments/${assessmentId}/draft_report/`);
      return response.data;
    },
    enabled: !!assessmentId,
    retry: false,
    throwOnError: false,
  });
}

export function useAssessmentSignedReport(assessmentId: string) {
  return useQuery<SignedReport>({
    queryKey: ['assessmentSignedReport', assessmentId],
    queryFn: async () => {
      const response = await apiClient.get<SignedReport>(`/assessments/${assessmentId}/signed_report/`);
      return response.data;
    },
    enabled: !!assessmentId,
  });
}

export function useSignOffAssessment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ assessmentId, data }: { assessmentId: string; data: SignOffRequest }) => {
      const response = await apiClient.post(`/assessments/${assessmentId}/sign_off/`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['assessment', variables.assessmentId] });
    },
  });
}

export function useStartReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (assessmentId: string) => {
      const response = await apiClient.post(`/assessments/${assessmentId}/start_review/`);
      return response.data;
    },
    onSuccess: (_, assessmentId) => {
      queryClient.invalidateQueries({ queryKey: ['assessment', assessmentId] });
    },
  });
}

export function useUpdateConsent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ assessmentId, consentObtained }: { assessmentId: string; consentObtained: boolean }) => {
      const response = await apiClient.post(`/assessments/${assessmentId}/update_consent/`, {
        consent_obtained: consentObtained,
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['assessment', variables.assessmentId] });
    },
  });
}

export function useSaveReflections() {
  return useMutation({
    mutationFn: async ({ assessmentId, notes }: { assessmentId: string; notes: string[] }) => {
      const response = await apiClient.post(`/assessments/${assessmentId}/save_reflections/`, { notes });
      return response.data;
    },
  });
}

export function usePromptTemplates() {
  return useQuery<PromptTemplate[]>({
    queryKey: ['promptTemplates'],
    queryFn: async () => {
      const response = await apiClient.get<PromptTemplate[] | { results: PromptTemplate[] }>('/assessments/prompts/');
      if (response.data && typeof response.data === 'object' && 'results' in response.data) {
        return response.data.results;
      }
      return response.data;
    },
  });
}

export interface CreatePromptTemplateRequest {
  name: string;
  mode: AssessmentMode;
  age_band: string;
  instructions: string;
  time_limit_seconds: number;
  requires_topic: boolean;
  is_active: boolean;
  is_default: boolean;
}

export function useCreatePromptTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreatePromptTemplateRequest): Promise<PromptTemplate> => {
      const response = await apiClient.post<PromptTemplate>('/assessments/prompts/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promptTemplates'] });
    },
  });
}
